import React, { Suspense, lazy, useState, useEffect, createContext, useMemo, memo } from 'react';
import { Outlet } from 'react-router-dom';
import { GlobalStyle } from './styles/StyledComponents';
import { detectUserLocation } from './services/locationService';
import { initGA } from './utils/analytics';

// Lazy load Navigation component
const Navigation = lazy(() => import('./components/Navigation'));

export const LocationContext = createContext();

// Create a memoized loading indicator
const LoadingIndicator = memo(() => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
  </div>
));

// Create a memoized navigation fallback
const NavigationFallback = memo(() => (
  <div className="h-16 bg-white shadow-sm animate-pulse"></div>
));

// Create a memoized content fallback
const ContentFallback = memo(() => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-pulse space-y-4">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
    </div>
  </div>
));

function App() {
  const [userLocation, setUserLocation] = useState('US');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize GA only once in production
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      initGA();
    }
  }, []);

  // Detect user location
  useEffect(() => {
    let isMounted = true;
    
    const initializeLocation = async () => {
      try {
        const detectedLocation = await detectUserLocation();
        if (isMounted) {
          setUserLocation(detectedLocation);
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        // Default to US if location detection fails
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    initializeLocation();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const locationContextValue = useMemo(() => ({
    userLocation, 
    setUserLocation, 
    isLoading
  }), [userLocation, isLoading]);

  // Simple loading indicator
  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <LocationContext.Provider value={locationContextValue}>
      <GlobalStyle />
      <Suspense fallback={<NavigationFallback />}>
        <Navigation />
      </Suspense>
      <Suspense fallback={<ContentFallback />}>
        <Outlet />
      </Suspense>
    </LocationContext.Provider>
  );
}

export default memo(App);
